import { FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { getErrorResponseData } from '@app/routing/Session/ErrorResponse'

import { Form } from '@lib/formik'

import { useModal } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { FileData } from '@ui/components/FileUpload/types'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { WalletDto } from '@model/WalletDto'

import { FormTemplate } from '@global/templates/FormTemplate'

import { useMutation } from '@hooks/useMutation'

import { Loading } from '@shared/components/Loading/Loading'
import { ActionModal } from '@shared/modals/Action.modal'

import { PaymentAgentTransferClientDetailsFields } from './PaymentAgentTransferClientDetailsFields'
import { PaymentAgentTransferClientFields } from './PaymentAgentTransferClientFields'

export interface PaymentAgentTransferFormValues {
  clientWallet: ClientWallet
  paymentProviderWallet?: WalletDto
  tradingAccountId: string
  campaignName: string
  amount: number | undefined
  system: string
  account: string
  comment: string
  fileName: string
  fileData: FileData | undefined
  base64Content: string
}

export type ClientWallet =
  | WalletDto
  | {
      id: string
      name: string
    }

const useTransferMutate = () => {
  const { walletId } = useParams<{ walletId?: string }>()

  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()

  const { setValues } = useFormikContext<PaymentAgentTransferFormValues>()

  const handleSuccess = async (
    {
      paymentProviderWallet,
      clientWallet,
    }: { paymentProviderWallet: WalletDto; clientWallet: WalletDto },
    values: PaymentAgentTransferFormValues
  ) => {
    setValues({
      ...values,
      paymentProviderWallet,
      clientWallet,
    })
  }

  const handleError = (error: unknown) => {
    if (!(error instanceof ResponseError)) return

    const { errorCode } = getErrorResponseData(error)

    if (errorCode === 'client_payment_agent_link_does_not_exist') {
      error.preventDefault()
      addSnackbar.error({ message: t('errors.Client has not accepted power of attorney') })
    }
  }

  return useMutation(
    async (values: PaymentAgentTransferFormValues) => {
      const [paymentProviderWallet, clientWallet] = await Promise.all([
        apiClient.getWalletById(walletId || ''),
        apiClient.paymentAgentValidateClientDetails(walletId || '', values.clientWallet.name),
      ])

      return { paymentProviderWallet, clientWallet }
    },
    {
      onSuccess: handleSuccess,
      onError: handleError,
    }
  )
}

const PaymentAgentTransferFormUI: React.FC<
  FormikProps<PaymentAgentTransferFormValues> & OuterProps
> = (props) => {
  const { values, onCancel, handleSubmit } = props

  const { t } = useTranslation()
  const cancelModal = useModal(ActionModal, {
    onConfirm: onCancel,
  })

  const mutation = useTransferMutate()

  const handleSubmitForm = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleSubmit()
  }

  const handleValidateClientDetails = async (values: PaymentAgentTransferFormValues) => {
    await mutation.mutate(values)
  }

  return (
    <FormTemplate title={t('Payment Agent.Payment Agent Transfer')} goBack={cancelModal.open}>
      <Form onSubmit={handleSubmitForm}>
        <Loading isLoading={mutation.isLoading} showLoadingIcon>
          {!values?.clientWallet?.id && (
            <PaymentAgentTransferClientFields
              onSubmit={handleValidateClientDetails}
              onCancel={cancelModal.open}
            />
          )}
          {values?.clientWallet?.id && (
            <PaymentAgentTransferClientDetailsFields onCancel={cancelModal.open} />
          )}
        </Loading>
      </Form>
    </FormTemplate>
  )
}

const initialState = (data?: PaymentAgentTransferFormValues): PaymentAgentTransferFormValues => {
  return {
    clientWallet: {
      ...data?.clientWallet,
      id: data?.clientWallet?.id || '',
      name: data?.clientWallet?.name || '',
    },
    paymentProviderWallet: undefined,
    tradingAccountId: data?.tradingAccountId || '',
    campaignName: data?.campaignName || '',
    amount: data?.amount,
    system: data?.system || '',
    account: data?.account || '',
    comment: data?.comment || '',
    fileName: data?.fileName || '',
    fileData: undefined,
    base64Content: data?.base64Content || '',
  }
}

interface OuterProps {
  data: PaymentAgentTransferFormValues | undefined
  onSubmit(values: PaymentAgentTransferFormValues): void
  onCancel(): void
}

export const PaymentAgentTransferForm = withFormik<OuterProps, PaymentAgentTransferFormValues>({
  mapPropsToValues: ({ data }) => {
    return initialState(data)
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<PaymentAgentTransferFormValues> = {}
    const amount = values?.amount ?? 0

    if (!values?.clientWallet?.id) {
      if (!values?.clientWallet?.name) {
        errors.clientWallet = {}
        errors.clientWallet.name = t('Validation.Required')
      }
    }

    if (values?.clientWallet?.id) {
      if (amount <= 0) {
        errors.amount = t('Validation.Required')
      }

      if (!values?.system) {
        errors.system = t('Validation.Required')
      }

      if (!values?.account) {
        errors.account = t('Validation.Required')
      }

      if (!values?.fileName) {
        errors.fileName = t('Validation.Required')
      }

      if (!values?.base64Content) {
        errors.base64Content = t('Validation.Required')
      }
    }

    if (values.paymentProviderWallet) {
      if (amount > values.paymentProviderWallet.balance) {
        errors.amount = t('Validation.There is not enough balance in your wallet')
      }
    }

    return errors
  },
  enableReinitialize: true,
})(PaymentAgentTransferFormUI)
